import { useMemo } from 'react';

import type { VariantManagement } from '@polyai/stream/store/reducers/variant';

import useFeatureSwitch from '../useFeatureSwitch';
import useProject from '../useProject';
import { useTemplateProject } from './useTemplateProject';

const SAMPLE_PROJECT_ACCOUNT_ID = 'SAMPLE_PROJECT_ACCOUNT';

export const useTemplateEngine = (options?: {
  parent_template_id?: string;
  refetchInterval?: number;
}) => {
  const { isFeatureOn } = useFeatureSwitch();
  const templateEngineOn = isFeatureOn('template_engine');

  const { project } = useProject();

  const templateProjectId =
    options?.parent_template_id ?? project?.parent_template_id;
  const isTemplateEngineEnabled = !!(templateEngineOn && templateProjectId);

  const {
    data: templateProject,
    error: templateProjectError,
    isLoading: templateProjectLoading,
  } = useTemplateProject({
    accountId: SAMPLE_PROJECT_ACCOUNT_ID,
    projectId: templateProjectId!,
    options: {
      enabled: templateEngineOn && !!templateProjectId,
      refetchInterval: options?.refetchInterval,
    },
  });

  const isBookingSetupPending = useMemo(() => {
    if (!isTemplateEngineEnabled || !project?.created_at) {
      return false;
    }
    const ONE_HOUR_MS = 60 * 60 * 1000;
    return Date.now() - new Date(project.created_at).getTime() < ONE_HOUR_MS;
  }, [isTemplateEngineEnabled, project?.created_at]);

  // Optimized function to get a map of variant ID to display name
  const getVariantDisplayNamesMap = useMemo(() => {
    return (
      envVariantManagement: VariantManagement | null,
    ): Record<string, string> => {
      if (!templateProject || !envVariantManagement) {
        return {};
      }

      const nameAttributeName =
        templateProject.overrides_schema?.sites?.nameAttribute || 'site_name';
      const nameAttribute = Object.values(
        envVariantManagement.attributes?.entities || {},
      ).find((attr: any) => attr.name === nameAttributeName);

      const variants = envVariantManagement.variants?.entities || {};

      if (!nameAttribute) {
        // Fallback to variant names
        return Object.fromEntries(
          Object.entries(variants).map(([id, variant]) => [
            id,
            variant.name || id,
          ]),
        );
      }

      // Use template attribute values
      const map: Record<string, string> = {};
      for (const [variantId, variant] of Object.entries(variants)) {
        const variantValues =
          envVariantManagement.variantAttributeValues?.entities[variantId];
        const displayName =
          variantValues?.values[nameAttribute.id] || variant.name || variantId;
        map[variantId] = displayName;
      }

      return map;
    };
  }, [templateProject]);

  return {
    isTemplateEngineEnabled,
    isBookingSetupPending,
    templateProject,
    templateProjectError,
    templateProjectLoading,
    getVariantDisplayNamesMap,
  };
};
